import React, { useEffect, useState } from 'react';
import { runLoginConnectivityChecks } from '../../services/loginConnectivityCheck';
import './LoginConnectivityPopup.css';

const STEPS = [
  {
    id: 'internet',
    label: 'Internet Connection',
    failDetail: 'No internet connection',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
      </svg>
    ),
  },
  {
    id: 'organization',
    label: 'Organization Check',
    failDetail: 'Your organization proxy may be blocking Workloop',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    id: 'service',
    label: 'Workloop Service',
    failDetail: 'Workloop service is unreachable',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
];

const StatusMark = ({ status }) => {
  if (status === 'checking' || status === 'pending') {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
        <svg className={`h-4 w-4 text-primary-600 dark:text-primary-400 ${status === 'checking' ? 'login-diag-spinner' : 'opacity-30'}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </span>
    );
  }

  if (status === 'ok') {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path className="login-diag-check-path" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }

  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400">
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path className="login-diag-x-path" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
  );
};

const LoginConnectivityPopup = ({ open, onClose, onComplete }) => {
  const [results, setResults] = useState({
    internet: 'pending',
    organization: 'pending',
    service: 'pending',
  });
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setFinished(false);
    setResults({ internet: 'pending', organization: 'pending', service: 'pending' });

    runLoginConnectivityChecks((partial) => {
      if (!cancelled) {
        setResults((prev) => ({ ...prev, ...partial }));
      }
    }).then((finalResults) => {
      if (!cancelled) {
        setFinished(true);
        onComplete?.(finalResults);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="login-diag-overlay fixed inset-0 z-[3000] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-diag-title"
    >
      <div className="login-diag-card w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between gap-3 mb-6">
          <h2
            id="login-diag-title"
            className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-500 tracking-tight"
          >
            Something is wrong
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <ul className="space-y-3">
          {STEPS.map((step, index) => {
            const status = results[step.id] || 'pending';
            const failed = status === 'fail';
            return (
              <li
                key={step.id}
                data-index={index}
                className={`login-diag-row flex items-center gap-3 rounded-xl border px-3.5 py-3 ${
                  failed
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'
                }`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  failed
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300'
                    : 'bg-white text-primary-600 dark:bg-gray-800 dark:text-primary-400'
                }`}>
                  {step.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {status === 'checking' && 'Checking…'}
                    {status === 'pending' && 'Waiting…'}
                    {status === 'ok' && 'All good'}
                    {failed && step.failDetail}
                  </p>
                </div>
                <StatusMark status={status} />
              </li>
            );
          })}
        </ul>

        {finished && (
          <div className="login-diag-footer mt-5">
            <p className="text-sm text-center text-gray-700 dark:text-gray-200 leading-relaxed">
              It seems something is not correct. Please try once again.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold py-2.5 transition-colors"
            >
              Try once again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginConnectivityPopup;
